import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useUserList } from "../../api/admin/user/hooks"; 
import UserProfileSimple from "../molecules/UserProfileSimple";
import ListTable from "../molecules/ListTable";
import Button from "../atoms/Button";
import { Input, InputWrap, Textarea } from "../atoms/Input";
import { SelectList } from "../atoms/Select";
import IconCircle from "../atoms/IconCircle"
import { showConfirm } from "../../reducer/confirmSlice";

/**
 * 그룹 상세 폼 (조회/생성/수정)
 * @param initData { title?: string, description?: string, groupUsers?: [] }
 * @param editable Input 필드 수정 가능 유무
 * @param isModify 수정/생성 모드 유무
 */
const GroupForm = ({  
        initData = {},
        editable = true,
        isModify = true,
        onSubmit,
        onDelete,
}) => {
    const dispatch = useDispatch()

    // initData 내부 필드 구조분해 및 기본값 지정
    const {
        title: initTitle = "", 
        description: initDescription = "",
        groupUsers: initGroupUsers = []
    } = initData
    // 초기화시 기본값 사용
    const [title, setTitle] = useState(initTitle)
    const [description, setDescription] = useState(initDescription)
    // 그룹에 포함할 유저 SelectList 를 위한 객체 (reviewees[idx: 0].reviewers 에 selected user idx 객체들이 담긴다)
    const [groupUsers, setGroupUsers] = useState([{
        idx: 0, reviewers: initGroupUsers.map(u => ({ idx: u.idx }))
    }])
    
    useEffect(() => {
        // initData 가 바뀌면 state 도 동기화 - 수정 모드에서만 동작하도록
        if (isModify) {
            setTitle(initTitle)
            setDescription(initDescription)
            setGroupUsers([
                { idx: 0, reviewers: initGroupUsers.map(u => ({ idx: u.idx })) }
            ])
        }
    }, [initTitle, initDescription, initGroupUsers])

    const user = useSelector(state => state.auth.user)
    // 유저 권한 - 0: 일반, 1: 중간, 2: 최고
    const [userGrade] = useState(user.grade)

    // 전체 유저 목록
    const { data: userData } = useUserList()
    const userList = userData?.data || []
    
    const isFormValid = (title ?? "").trim().length > 0 && (description ?? "").trim().length > 0
    const [submitting, setSubmitting] = useState(false)

    const groupUserSelectOnChangeHandler = (_, idx, optIdx) => {
        setGroupUsers(prev => {
            const copy = JSON.parse(JSON.stringify(prev))
            // 기존 reviewers 배열
            const cur = copy[idx].reviewers || []
            const exists = cur.find(r=>r.idx === optIdx)
            // 토글 (아이템이 이미 있으면 제거, 없으면 추가)
            copy[idx].reviewers = exists ? cur.filter(r=>r.idx!==optIdx): [...cur, { idx:optIdx }]
            return copy
        })
    }

    const submitHandler = async e => {
        e.preventDefault()

        if (!isFormValid || !onSubmit) return
        
        setSubmitting(true)
        const selectedUserList = groupUsers[0].reviewers.map(r => ({ idx: r.idx.toString() }))

        try {
            await onSubmit({ title, description, selectedUserList })
        } finally {
            setSubmitting(false)
        }
    }

    const deleteHandler = e => {
        dispatch(showConfirm({
            msg: "그룹을 삭제할까요?",
            func: () => {
                onDelete()
            }
        }))
    }

    return (
        <Container>
            <Wrap col gap={"25px"} className="inner">
                {/* 관리자용 UI - 그룹 정보 수정 */}
                {((userGrade === "1" || userGrade === "2") && editable) ? (
                    <form onSubmit={submitHandler}>
                        <Wrap col gap={"20px"}>
                            <div className="header">
                                <h2>{isModify ? "그룹 정보" : "그룹 생성"}</h2>
                                {(isModify && userGrade === "2") && (
                                    <IconCircle className="bi-trash3" onClick={deleteHandler} />
                                )}
                            </div>
                            <Wrap col gap={"15px"} className="form-card">
                                <InputWrap title={"그룹명"}>
                                    <Input 
                                        name={"title"} 
                                        value={title} 
                                        placeholder={`그룹명 입력`} 
                                        onChange={e => setTitle(e.target.value)} 
                                        disabled={userGrade !== "2"} />
                                </InputWrap>
                                <InputWrap title={"그룹 설명"}>
                                    <Textarea 
                                        name={"description"} 
                                        value={description} 
                                        placeholder={`그룹 설명 입력`} 
                                        onChange={e => setDescription(e.target.value)} 
                                        disabled={userGrade !== "2"} />
                                </InputWrap>

                                <Wrap row gap={"20px"}>
                                    <SelectList 
                                        type="user" 
                                        data={userList} 
                                        onChange={groupUserSelectOnChangeHandler} 
                                        idx={0} 
                                        stateValue={[{reviewees: groupUsers}, setGroupUsers]} />

                                    <Button 
                                        type="deep" 
                                        disabled={submitting || !isFormValid} 
                                        onClick={submitHandler} 
                                        style={{ width: "80px", marginLeft: "auto" }}>
                                        {submitting ? "저장중…" : "저장"}
                                    </Button>
                                </Wrap>
                            </Wrap>
                        </Wrap>
                    </form>
                ) : (
                    // 일반 사용자용 UI - 그룹 정보 출력
                    <>
                        <h2>{title}</h2>
                        <div className="title-desc">{description}</div>
                    </>
                )}

                {/* 그룹에 속한 유저 목록 */}
                {groupUsers[0]?.reviewers?.length > 0 ? (
                    <ListTable>
                        <thead>
                            <tr>
                                <th style={{ width: "10%" }} className="center">번호</th>
                                <th style={{ width: "90%" }}>Knotter</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupUsers[0].reviewers.map((el, i) => {
                                // 전체 userList에서 reviewers(선택한 그룹 userList) 각 아이템의 idx가 같은 유저 정보를 찾아 사용
                                const user = userList.find(u => u.idx === el.idx)
                                return (
                                    <tr key={el.idx}>
                                        <td className="seq">{i + 1}</td>
                                        <td>
                                            <UserProfileSimple user={user} />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </ListTable>
                ) : (
                    <div className="empty-group-member-msg">그룹에 소속된 Knotter가 없습니다.</div>
                )}
            </Wrap>
        </Container>
    )
}

const Wrap = styled.div`
    display: flex;
    width: 100%;

    label:not(:has(input[type="radio"])) {
        width: 150px;
    }

    input,
    textarea {
        padding: 10px;
        width: 100%;
    }

    ${(props) => props.col && `flex-direction: column;`}
    ${(props) => props.row && `flex-direction: row;`}
    ${(props) => props.gap && `gap: ${props.gap}`}
`

const Container = styled.div`
    display: flex;

    .inner {
        display: flex;
        flex-direction: column;
        width: 800px;
        gap: 30px;
    }
    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .desc {
        color: var(--gray-color);
    }
    .empty-group-member-msg {
        font-size: 16px;
        letter-spacing: -0.01em;
        border: 1px solid var(--gray-color);
        background: var(--base-color);
        padding: 24px;
        margin: 20px 0;
        border-radius: 8px;
        text-align: center;
        color: var(--text-color);
    }
`

export default GroupForm